import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCarriers, deleteCarrier, patchCarrier, logError } from '../api';
import { Button, Table, Container, Row, Col } from 'react-bootstrap';
import DeleteConfirmationModal from './Global/DeleteConfirmationModal';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import SortableHeader from './ui/SortableHeader';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import { getDisabledRowClass } from '../utils/disabledUtils';
import './Global/Style/SalesRepsList.css';

const CarrierList = () => {
    const [carriers, setCarriers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [carrierToDelete, setCarrierToDelete] = useState(null);
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();

    const loadCarriers = useCallback(async () => {
        try {
            const data = await getCarriers(includeDisabled, orderBy, orderDesc);
            setCarriers(data);
        } catch (error) {
            logError(error);
            console.error('Error loading carriers:', error);
        }
    }, [includeDisabled, orderBy, orderDesc]);

    useEffect(() => {
        loadCarriers();
    }, [loadCarriers]);

    const handleDeleteConfirm = () => {
        if (carrierToDelete) {
            deleteCarrier(carrierToDelete)
                .then(() => {
                    setCarriers(carriers.filter(carrier => carrier.id !== carrierToDelete));
                    setShowModal(false);
                })
                .catch(err => logError(err));
        }
    };

    const handleToggleStatus = async (carrierId, currentStatus) => {
        try {
            await patchCarrier(carrierId, { id: carrierId, disabled: !currentStatus });
            setCarriers((prevCarriers) => 
                prevCarriers.map((carrier) => 
                    carrier.id === carrierId 
                        ? { ...carrier, disabled: !currentStatus }
                        : carrier
                )
            );
        } catch (error) {
            logError(error);
            console.error('Error toggling carrier status:', error);
        }
    };

    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
                <Row className="mb-4">
                    <Col xs={12} md={6}>
                        <h1>Transportistas</h1>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                        <DisabledToggle 
                            includeDisabled={includeDisabled}
                            onChange={setIncludeDisabled}
                        />
                        <Button variant="primary" data-testid="new-carrier-button">
                            <Link to="/carriers/new" style={{ color: 'white', textDecoration: 'none' }}>
                                Nuevo Transportista
                            </Link>
                        </Button>
                    </Col>
                </Row>

            <Table responsive className="table">
                <thead>
                    <tr>
                        <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>Id</SortableHeader>
                        <SortableHeader column="firstName" onSort={handleSort} getSortIcon={getSortIcon}>Nombre</SortableHeader>
                        <th>Usuario</th>
                        <SortableHeader column="phone" onSort={handleSort} getSortIcon={getSortIcon}>Teléfono</SortableHeader>
                        <SortableHeader column="email" onSort={handleSort} getSortIcon={getSortIcon}>Email</SortableHeader>
                        <th className="text-center">Color</th>
                        <th>¿Es jefe?</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {carriers.map(carrier => (
                        <tr key={carrier.id} className={getDisabledRowClass(carrier.disabled)}>
                            <td>{carrier.id}</td>
                            <td>{carrier.firstName} {carrier.lastName1} {carrier.lastName2}</td>
                            <td>{carrier.user?.username || ''}</td>
                            <td>{carrier.phone}</td>
                            <td>{carrier.email}</td>
                            <td className="text-center align-middle"> {/* Centrar el contenido */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                    <div style={{ width: '25px', height: '25px', backgroundColor: carrier.colourHex, border: '1px solid #000', borderRadius: '50%' }}></div>
                                </div>
                            </td>
                            <td data-label="¿Es Jefe?">{carrier.user?.roles?.some(r => r.id === 7) ? 'Sí' : 'No'}</td>
                            <td>
                                <div className="d-flex">
                                    <Button variant="warning" className="me-2" as={Link} to={`/carriers/edit/${carrier.id}`} size="sm" data-testid={`edit-carrier-${carrier.id}`}>
                                        Editar
                                    </Button>
                                    <Button
                                        variant={carrier.disabled ? "success" : "secondary"}
                                        onClick={() => handleToggleStatus(carrier.id, carrier.disabled)}
                                        size="sm"
                                        className="me-2"
                                        data-testid={`toggle-carrier-${carrier.id}`}
                                    >
                                        {carrier.disabled ? "Habilitar" : "Deshabilitar"}
                                    </Button>
                                    <Button 
                                        variant="danger" 
                                        onClick={() => { setCarrierToDelete(carrier.id); setShowModal(true); }}
                                        size="sm"
                                        data-testid={`delete-carrier-${carrier.id}`}
                                    >
                                        Eliminar
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

                <DeleteConfirmationModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onConfirm={handleDeleteConfirm}
                    message="¿Estás seguro de que deseas eliminar este transportista? Esta acción no se puede deshacer."
                />
            </Container>
        </>
    );
};

export default CarrierList;